import { useState, useCallback } from "react";

import { useAuth } from "@hl/shared-features/lib/features/auth/AuthContext";
import { useOnChainTransaction } from "@hl/shared-features/lib/hooks/useOnChainTransaction";
import { logError } from "@hl/shared-features/lib/services/logger";

import { TransactionType } from "apollo/graphql.generated";
import { isSeriesLikeCollection } from "~/features/collections/displayUtils";
import { GetManageCollectionQuery } from "~/features/manage-collection/v2.queries.graphql.generated";
import {
  GetManageTokenCollectionDocument,
  GetManageTokenMintVectorsDocument,
  useUpdateCollectionMetadataMutation,
  useUpdateCollectionOffChainMutation,
} from "~/features/tokens/sections/ManageToken/queries.graphql.generated";
const useUpdateMintVectorOnChain = ({
  collection,
  onFinish,
}: {
  collection: GetManageCollectionQuery["getManageCollection"];
  onFinish: () => void;
}) => {
  const [error, setError] = useState<string>("");
  const { walletAddress } = useAuth();

  const isSeries = isSeriesLikeCollection(collection.collectionType);

  const [updateCollectionOffChain, { loading: loadingOffChainUpdate }] =
    useUpdateCollectionOffChainMutation();

  const [updateCollectionOnChain, { loading: loadingOnChainArgs }] =
    useUpdateCollectionMetadataMutation();

  const {
    error: transactionError,
    startTransaction,
    buttonLabel,
    buttonLoading,
  } = useOnChainTransaction({
    collectionType: collection.collectionType,
    entityId: collection.id,
    onFinish,
    refetchQueries: [
      GetManageTokenCollectionDocument,
      GetManageTokenMintVectorsDocument,
    ],
    transactionType: TransactionType.EVM_UPDATE_METADATA,
  });

  const editDetails = useCallback(
    async ({ name, description }: { name: string; description: string }) => {
      setError("");
      if (!walletAddress) return;

      try {
        if (isSeries) {
          await updateCollectionOffChain({
            variables: {
              collectionId: collection.id,
              data: {
                name,
                description,
              },
            },
          });
          onFinish();
        } else {
          const metadataArgs = await updateCollectionOnChain({
            variables: {
              collectionId: collection.id,
              data: {
                name,
                description,
              },
            },
          });
          const args = metadataArgs.data?.updateCollectionMetadata;
          if (!args) throw new Error("Error fetching args");
          await startTransaction({
            args,
            metadata: {
              collectionId: collection.id,
              nftContractAddress: walletAddress,
              txType: TransactionType.EVM_UPDATE_METADATA,
            },
          });
        }
      } catch (e) {
        setError("Failed to save changes.");
        await logError({
          e,
          message: "[EditDetail] Failed to update",
        });
      }
    },
    [walletAddress, collection.id]
  );

  return {
    error: error || transactionError,
    editDetails,
    buttonLabel,
    buttonLoading: buttonLoading || loadingOffChainUpdate || loadingOnChainArgs,
    isOnChainTransaction: !isSeries,
  };
};

export default useUpdateMintVectorOnChain;
